import { Injectable } from '@angular/core';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import {
    EntityType,
    MapItemType,
    RiderColor,
} from '@polaris-customerweb/sharedcomponents.core';
import { SearchResult } from '@polaris-customerweb/sharedcomponents.search';
import {
    Geofence,
    PlannedRide,
    TrackedRide,
    UserContentItem,
    Waypoint,
} from '@polaris-customerweb/sharedcomponents.user-content';
import { UserProfileService } from '@polaris-customerweb/sharedcomponents.user-profile';
import { flatten } from 'lodash-es';
import { firstValueFrom, forkJoin, Observable, of, throwError } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { Collection } from 'src/app/models/collection';
import { CommunityItem } from 'src/app/models/community-item';
import { RideEvent } from 'src/app/models/ride-event';
import { Share } from 'src/app/models/share';
import { InProgressRideService } from '../state/in-progress-ride/in-progress-ride.service';
import { UserAuthService } from '../state/user-auth/user-auth.service';
import { CollectionService } from './collection.service';
import { CommunityItemService } from './community-item.service';
import { GeofenceService } from './geofence.service';
import { PlannedRideService } from './planned-ride.service';
import { RideEventService } from './ride-event.service';
import { ShareService } from './share.service';
import { TrackedRideService } from './tracked-ride.service';
import { WaypointService } from './waypoint.service';

export class GetUserContentResult {
    userContentItem: UserContentItem | undefined;
    communityItem?: CommunityItem;
    share?: Share;
    rideEvent?: RideEvent;
    collection?: Collection;
    isCommunityItem = false;
    isCommunityItemChild = false;
    isShare = false;
    isShareChild = false;

    constructor(userContentItem?: UserContentItem) {
        this.userContentItem = userContentItem;
    }
}

@Injectable({ providedIn: 'root' })
export class UserContentService {
    private profileColor: RiderColor;

    constructor(
        private collectionService: CollectionService,
        private communityItemService: CommunityItemService,
        private configService: ConfigService,
        private geofenceService: GeofenceService,
        private inProgressRideService: InProgressRideService,
        private plannedRideService: PlannedRideService,
        private rideEventService: RideEventService,
        private shareService: ShareService,
        private trackedRideService: TrackedRideService,
        private userAuthService: UserAuthService,
        userProfileService: UserProfileService,
        private waypointService: WaypointService
    ) {
        firstValueFrom(userProfileService.getUserProfile$())
            .then((profile) => {
                this.profileColor = profile?.riderColor || 'Blue';
            })
            .catch(() => {
                this.profileColor = 'Blue';
            });
    }

    static GetViewOnMapUrl(
        userContentId: string,
        entityType: EntityType,
        collectionId?: string,
        communityId?: string,
        rideEventId?: string,
        shareId?: string
    ): string {
        let entityTypeUrlFragment = '';

        switch (entityType) {
            case 'Collection':
                entityTypeUrlFragment = 'collection';
                break;
            case 'Event':
                entityTypeUrlFragment = 'ride-event';
                break;
            case 'Geofence':
                entityTypeUrlFragment = 'geofence';
                break;
            case 'PlannedRide':
                entityTypeUrlFragment = 'planned-ride';
                break;
            case 'TrackedRide':
                entityTypeUrlFragment = 'tracked-ride';
                break;
            case 'Waypoint':
                entityTypeUrlFragment = 'waypoint';
        }

        return (
            `/map` +
            (shareId ? `/share/${shareId}` : ``) +
            (communityId ? `/community/${communityId}` : ``) +
            (rideEventId ? `/ride-event/${rideEventId}` : ``) +
            (collectionId ? `/collection/${collectionId}` : ``) +
            `/${entityTypeUrlFragment}/${userContentId}`
        );
    }

    getIconClass(item: UserContentItem | SearchResult, isCommunity: boolean) {
        if (isCommunity) {
            return this.getIconClassByBrand();
        } else if (item.color) {
            return item.type === 'Waypoint' && (item as Waypoint).isPublic
                ? 'brown'
                : item.color.toLowerCase();
        } else if (item.type === 'PublicWaypoint') {
            return 'brown';
        } else if (item.type === 'Collection' || item.type === 'Event') {
            return this.profileColor.toLowerCase();
        } else if (item.type === 'PlannedRide' || item.type === 'Geofence') {
            return this.getIconClassByBrand();
        } else if (item.type === 'Location') {
            return '';
        } else {
            // Tracked Ride or Waypoint
            return 'blue';
        }
    }

    getUserContentFromState(
        userContentId: string,
        entityType: EntityType,
        collectionId?: string,
        communityId?: string,
        rideEventId?: string,
        shareId?: string
    ): Observable<GetUserContentResult | undefined> {
        if (!userContentId) {
            return of(undefined);
        } else if (shareId && rideEventId && collectionId) {
            return this.getUserContentFromSharedRideEventCollection(
                shareId,
                collectionId,
                userContentId,
                entityType
            );
        } else if (shareId && collectionId) {
            return this.getUserContentFromSharedCollection(
                shareId,
                userContentId,
                entityType
            );
        } else if (shareId && rideEventId) {
            return this.getUserContentFromSharedRideEvent(
                shareId,
                userContentId,
                entityType
            );
        } else if (shareId) {
            return this.getUserContentFromShare(shareId);
        } else if (communityId && rideEventId && collectionId) {
            return this.getUserContentFromCommunityRideEventCollection(
                communityId,
                collectionId,
                userContentId,
                entityType
            );
        } else if (communityId && collectionId) {
            return this.getUserContentFromCommunityCollection(
                communityId,
                userContentId,
                entityType
            );
        } else if (communityId && rideEventId) {
            return this.getUserContentFromCommunityRideEvent(
                communityId,
                userContentId,
                entityType
            );
        } else if (communityId) {
            return this.getUserContentFromCommunityItem(communityId);
        } else if (rideEventId && collectionId) {
            return this.getUserContentFromPrivateRideEventCollection(
                rideEventId,
                collectionId,
                userContentId,
                entityType
            );
        } else if (rideEventId) {
            return this.getUserContentFromPrivateRideEvent(
                rideEventId,
                userContentId,
                entityType
            );
        } else if (collectionId) {
            return this.getUserContentFromCollection(
                collectionId,
                userContentId,
                entityType
            );
        } else {
            return this.userAuthService.isAuthenticated$.pipe(
                take(1),
                switchMap((isAuthenticated) => {
                    if (!isAuthenticated) {
                        return throwError(() => new Error('not authenticated'));
                    }

                    return this.getUserContentItem(userContentId, entityType);
                })
            );
        }
    }

    getPublicWaypointFromState(
        waypointId: string
    ): Observable<GetUserContentResult> {
        return this.waypointService
            .getPublicWaypoint(waypointId)
            .pipe(map((waypoint) => new GetUserContentResult(waypoint)));
    }

    getLoggedInUsersContent(): Observable<{
        collections: Collection[];
        geofences: Geofence[];
        plannedRides: PlannedRide[];
        trackedRides: TrackedRide[];
        waypoints: Waypoint[];
    }> {
        return forkJoin([
            this.collectionService.getCollections$().pipe(take(1)),
            this.configService.featureFlags.areGeofencesEnabled
                ? this.geofenceService.getGeofences$().pipe(take(1))
                : of([]),
            this.plannedRideService.getPlannedRides$().pipe(take(1)),
            this.trackedRideService.getTrackedRides$().pipe(take(1)),
            this.waypointService.getWaypoints$().pipe(take(1)),
        ]).pipe(
            map(
                ([
                    collections,
                    geofences,
                    plannedRides,
                    trackedRides,
                    waypoints,
                ]) => {
                    return {
                        collections,
                        geofences,
                        plannedRides,
                        trackedRides,
                        waypoints,
                    };
                }
            )
        );
    }

    isDownloadable(userContentItem: UserContentItem): boolean {
        return (
            !(userContentItem instanceof Collection) || userContentItem.hasItems
        );
    }

    async getDetailUrl(
        type: MapItemType,
        id: string,
        groupRideId?: string
    ): Promise<string[]> {
        const segment = groupRideId
            ? ['map', 'ride-group', groupRideId]
            : 'map';
        switch (type) {
            case 'adventure':
                return flatten([segment, 'adventure', id]);
            case 'dealer':
                return flatten([segment, 'dealer', id]);
            case 'geofence':
                return flatten([segment, 'geofence', id]);
            case 'inProgressRide':
                return flatten([
                    segment,
                    await this.inProgressRideService.getUrlSegmentsForInProgressRide(
                        id
                    ),
                ]);
            case 'MixedLand':
            case 'PrivateLand':
                return flatten([segment, 'parcels-information', id]);
            case 'plannedRide':
                return flatten([segment, 'planned-ride', id]);
            case 'poi':
            case 'premiumPoi':
                return flatten([segment, 'poi', id]);
            case 'publicWaypoint':
                return flatten([segment, ['waypoint', 'public'], id]);
            case 'rideArea':
                return flatten([segment, 'ride-area', id]);
            case 'trackedRide':
                return flatten([segment, 'tracked-ride', id]);
            case 'vehicle':
                return flatten([segment, 'vehicle', id]);
            case 'waypoint':
                return flatten([segment, 'waypoint', id]);
            case 'rideCondition':
            case 'rideGroupRider':
                // no url
                return [];
            case 'trail':
                return flatten([segment, 'trail', id]);
            default:
                return groupRideId ? [] : ['map'];
        }
    }

    private getIconClassByBrand(): string {
        return 'primary-icon-color';
    }

    private getUserContentFromSharedRideEventCollection(
        shareId: string,
        collectionId: string,
        userContentId: string,
        entityType: EntityType
    ): Observable<GetUserContentResult> {
        return this.shareService.getFullShare(shareId).pipe(
            map((share) => {
                const eventCollection =
                    share.content.event.getCollection(collectionId);
                const result = new GetUserContentResult(
                    eventCollection?.getUserContentItem(
                        userContentId,
                        entityType
                    )
                );
                result.share = share;
                result.rideEvent = share.content.event;
                result.collection = eventCollection;
                result.isShareChild = true;
                return result;
            })
        );
    }

    private getUserContentFromSharedCollection(
        shareId: string,
        userContentId: string,
        entityType: EntityType
    ): Observable<GetUserContentResult> {
        return this.shareService.getFullShare(shareId).pipe(
            map((share) => {
                const result = new GetUserContentResult(
                    share.content.collection.getUserContentItem(
                        userContentId,
                        entityType
                    )
                );
                result.share = share;
                result.collection = share.content.collection;
                result.isShareChild = true;
                return result;
            })
        );
    }

    private getUserContentFromSharedRideEvent(
        shareId: string,
        userContentId: string,
        entityType: EntityType
    ): Observable<GetUserContentResult> {
        return this.shareService.getFullShare(shareId).pipe(
            map((share) => {
                const result = new GetUserContentResult(
                    share.content.event.getUserContentItem(
                        userContentId,
                        entityType
                    )
                );
                result.share = share;
                result.rideEvent = share.content.event;
                result.isShareChild = true;
                return result;
            })
        );
    }

    private getUserContentFromShare(
        shareId: string
    ): Observable<GetUserContentResult> {
        return this.shareService.getFullShare(shareId).pipe(
            map((share) => {
                const result = new GetUserContentResult(
                    share.content.contentItem
                );
                result.share = share;
                result.isShare = true;
                return result;
            })
        );
    }

    private getUserContentFromCommunityRideEventCollection(
        communityId: string,
        collectionId: string,
        userContentId: string,
        entityType: EntityType
    ): Observable<GetUserContentResult> {
        return this.communityItemService.getFullCommunityItem(communityId).pipe(
            map((communityItem) => {
                const eventCollection =
                    communityItem.content.event.getCollection(collectionId);
                const result = new GetUserContentResult(
                    eventCollection?.getUserContentItem(
                        userContentId,
                        entityType
                    )
                );
                result.communityItem = communityItem;
                result.rideEvent = communityItem.content.event;
                result.collection = eventCollection;
                result.isCommunityItemChild = true;
                return result;
            })
        );
    }

    private getUserContentFromCommunityCollection(
        communityId: string,
        userContentId: string,
        entityType: EntityType
    ): Observable<GetUserContentResult> {
        return this.communityItemService.getFullCommunityItem(communityId).pipe(
            map((communityItem) => {
                const result = new GetUserContentResult(
                    communityItem.content.collection.getUserContentItem(
                        userContentId,
                        entityType
                    )
                );
                result.communityItem = communityItem;
                result.collection = communityItem.content.collection;
                result.isCommunityItemChild = true;
                return result;
            })
        );
    }

    private getUserContentFromCommunityRideEvent(
        communityId: string,
        userContentId: string,
        entityType: EntityType
    ): Observable<GetUserContentResult> {
        return this.communityItemService.getFullCommunityItem(communityId).pipe(
            map((communityItem) => {
                const result = new GetUserContentResult(
                    communityItem.content.event.getUserContentItem(
                        userContentId,
                        entityType
                    )
                );
                result.communityItem = communityItem;
                result.rideEvent = communityItem.content.event;
                result.isCommunityItemChild = true;
                return result;
            })
        );
    }

    private getUserContentFromCommunityItem(
        communityId: string
    ): Observable<GetUserContentResult> {
        return this.communityItemService.getFullCommunityItem(communityId).pipe(
            map((communityItem) => {
                const result = new GetUserContentResult(
                    communityItem.content.contentItem
                );
                result.communityItem = communityItem;
                result.isCommunityItem = true;
                return result;
            })
        );
    }

    private getUserContentFromPrivateRideEventCollection(
        rideEventId: string,
        collectionId: string,
        userContentId: string,
        entityType: EntityType
    ): Observable<GetUserContentResult> {
        return this.rideEventService.getFullRideEvent(rideEventId).pipe(
            map((rideEvent) => {
                const eventCollection = rideEvent.getCollection(collectionId);
                const result = new GetUserContentResult(
                    eventCollection?.getUserContentItem(
                        userContentId,
                        entityType
                    )
                );
                result.rideEvent = rideEvent;
                result.collection = eventCollection;
                return result;
            })
        );
    }

    private getUserContentFromPrivateRideEvent(
        rideEventId: string,
        userContentId: string,
        entityType: EntityType
    ): Observable<GetUserContentResult> {
        return this.rideEventService.getFullRideEvent(rideEventId).pipe(
            map((rideEvent) => {
                const result = new GetUserContentResult(
                    rideEvent.getUserContentItem(userContentId, entityType)
                );
                result.rideEvent = rideEvent;
                return result;
            })
        );
    }

    private getUserContentFromCollection(
        collectionId: string,
        userContentId: string,
        entityType: EntityType
    ): Observable<GetUserContentResult> {
        return this.collectionService
            .getCollectionWithContents(collectionId)
            .pipe(
                map((collection) => {
                    const result = new GetUserContentResult(
                        collection.getUserContentItem(userContentId, entityType)
                    );
                    result.collection = collection;
                    return result;
                })
            );
    }

    private getUserContentItem(
        userContentId: string,
        entityType: EntityType
    ): Observable<GetUserContentResult> {
        switch (entityType) {
            case 'Collection':
                return this.collectionService
                    .getCollectionWithContents(userContentId)
                    .pipe(
                        map(
                            (collection) => new GetUserContentResult(collection)
                        )
                    );
            case 'CommunityItem':
                return this.communityItemService
                    .getFullCommunityItem(userContentId)
                    .pipe(
                        map((communityItem) => {
                            return new GetUserContentResult(communityItem);
                        })
                    );
            case 'Event':
                return this.rideEventService
                    .getFullRideEvent(userContentId)
                    .pipe(
                        map((rideEvent) => new GetUserContentResult(rideEvent))
                    );
            case 'Geofence':
                return this.geofenceService
                    .getGeofenceById(userContentId)
                    .pipe(
                        map((geofence) => new GetUserContentResult(geofence))
                    );
            case 'PlannedRide':
                return this.plannedRideService
                    .getPlannedRideById(userContentId)
                    .pipe(
                        map((plannedRide) => {
                            return new GetUserContentResult(plannedRide);
                        })
                    );
            case 'TrackedRide':
                return this.trackedRideService
                    .getTrackedRideById(userContentId)
                    .pipe(
                        map((trackedRide) => {
                            return new GetUserContentResult(trackedRide);
                        })
                    );
            case 'Waypoint':
                return this.waypointService
                    .getWaypoint(userContentId)
                    .pipe(
                        map((waypoint) => new GetUserContentResult(waypoint))
                    );
            default:
                return throwError(
                    () => new Error('user content type not supported')
                );
        }
    }
}
